import { HISTORY_EVENT_MAP, type HistoryEvent } from '../Contracts/HistoryEvent'
import { REQUEST_STATUS_MAP } from '../Contracts/RequestStatus'

export interface HistoryEntry {
    key: string
    event: { value: string; label: string; color: string }
    occurredAt: string | null
    /** Хто змінив (ПІБ) або null */
    changedBy: string | null
    /** Потреба, якої стосується подія (за executorUuid у payload) або null */
    need: string | null
    /** Перехід статусу: старий → новий (підписи з REQUEST_STATUS_MAP, невідомий — код) */
    transition: { from: string; fromColor: string; to: string; toColor: string } | null
    /** Додаткові рядки: «Диспетчер: A → B», «Причина: …» */
    lines: string[]
}

const DEFAULT_COLOR = '#8c8c8c'

const statusLabel = (status: unknown): { label: string; color: string } | null => {
    if (typeof status !== 'string' || status === '') return null
    const info = REQUEST_STATUS_MAP[status as keyof typeof REQUEST_STATUS_MAP]
    return info ? { label: info.label, color: info.color } : { label: status, color: DEFAULT_COLOR }
}

const personName = (person: any): string | null => person?.fullName || person?.shortName || person?.email || null

/**
 * Довідник імен диспетчерів за id з даних картки: виконавці потреб і відповідальний
 * диспетчер заявки. Payload історії зберігає лише id, назв у ньому немає.
 */
const buildDispatcherIndex = (values: any): Map<number, string> => {
    const index = new Map<number, string>()
    const add = (person: any) => {
        const name = personName(person)
        if (typeof person?.id === 'number' && name) index.set(person.id, name)
    }
    add(values?.responsibleDispatcher)
    ;(values?.vehicleNeeds ?? []).forEach((need: any) =>
        (need?.executors ?? []).forEach((ex: any) => add(ex?.dispatcher))
    )
    return index
}

/** Підпис потреби за uuid її виконавця: «Потреба 1 · Маніпулятор» */
const buildNeedIndex = (values: any): Map<string, string> => {
    const index = new Map<string, string>()
    ;(values?.vehicleNeeds ?? []).forEach((need: any, i: number) => {
        const label = `Потреба ${i + 1}${need?.vehicleKind?.title ? ` · ${need.vehicleKind.title}` : ''}`
        ;(need?.executors ?? []).forEach((ex: any) => {
            if (typeof ex?.uuid === 'string') index.set(ex.uuid, label)
        })
    })
    return index
}

/**
 * Записи історії заявки для таймлайну з «сирих» подій бекенда. Payload має три формати
 * переходу статусу — `{old:{status}, new:{status}}`, `{from, to}` і `{old, new}` рядками
 * (автоматичні переходи виконавця) — підтримуються всі.
 * `values` — дані картки (formContext.values) для імен диспетчерів, підписів потреб і
 * причини скасування, бо payload зберігає лише id.
 */
export const buildHistoryEntries = (history: any[], values: any): HistoryEntry[] => {
    const dispatchers = buildDispatcherIndex(values)
    const needs = buildNeedIndex(values)
    const dispatcherName = (id: unknown): string => (typeof id === 'number' ? (dispatchers.get(id) ?? `#${id}`) : '—')

    return (history ?? []).map((item: any, index: number) => {
        const payload = item?.payload ?? {}
        const eventInfo = HISTORY_EVENT_MAP[item?.event as HistoryEvent]
        const event = {
            value: String(item?.event ?? ''),
            label: eventInfo?.label ?? String(item?.event ?? '—'),
            color: eventInfo?.color ?? DEFAULT_COLOR,
        }

        const rawFrom = typeof payload?.old === 'string' ? payload.old : (payload?.old?.status ?? payload?.from)
        const rawTo = typeof payload?.new === 'string' ? payload.new : (payload?.new?.status ?? payload?.to)
        const from = statusLabel(rawFrom)
        const to = statusLabel(rawTo)
        const transition =
            from && to ? { from: from.label, fromColor: from.color, to: to.label, toColor: to.color } : null

        const lines: string[] = []
        if (item?.event === 'delegated') {
            const oldId = payload?.old?.dispatcher ?? payload?.old?.responsibleDispatcher
            const newId = payload?.new?.dispatcher ?? payload?.new?.responsibleDispatcher
            lines.push(`Диспетчер: ${dispatcherName(oldId)} → ${dispatcherName(newId)}`)
        }
        if (item?.event === 'dispatcher_assigned') {
            lines.push(`Диспетчер: ${dispatcherName(payload?.dispatcher)}`)
        }
        if (item?.event === 'cancelled') {
            const reasonId = payload?.new?.cancellationReason
            const reason = values?.cancellationReason
            if (reasonId !== undefined && reason?.id === reasonId && reason?.title)
                lines.push(`Причина: ${reason.title}`)
        }

        return {
            key: String(item?.uuid ?? item?.id ?? index),
            event,
            occurredAt: typeof item?.occurredAt === 'string' ? item.occurredAt : null,
            changedBy: personName(item?.changedBy),
            need: typeof payload?.executorUuid === 'string' ? (needs.get(payload.executorUuid) ?? null) : null,
            transition,
            lines,
        }
    })
}
